import { create } from 'zustand'

/**
 * LES RETOURS D'ACTION, en un endroit.
 *
 * Le produit disait « enregistré » sur les écrans de RÉGLAGES — une pastille
 * verte à côté du titre de la carte — et ne disait rien du tout partout
 * ailleurs. Or c'est l'inverse qu'il faut : dans un formulaire, le champ garde
 * la valeur qu'on vient de taper, donc l'écran prouve déjà quelque chose ; ce
 * qui a besoin d'un mot est l'action dont le résultat n'est PAS visible, et
 * plus encore celle qui fait disparaître ce sur quoi on a cliqué.
 *
 * Sortir quelqu'un du tableau d'exploitation en est l'exemple exact : la carte
 * s'en va, et rien ne distingue « c'est fait » de « ça a planté ».
 *
 * ── Deux règles, pour que ça reste lisible ────────────────────────────────
 *
 * Un ÉCHEC parle toujours. La moitié des gestes d'écriture du produit
 * n'attrapaient rien : une requête refusée laissait l'écran exactement comme
 * avant, ce qui se lit comme « le bouton ne marche pas » — le pire diagnostic,
 * parce qu'il fait recliquer.
 *
 * Un SUCCÈS ne parle que s'il n'est pas déjà visible. Déplacer une carte d'une
 * colonne à l'autre se voit ; annoncer « carte déplacée » à chaque glissement
 * transformerait le coin de l'écran en bruit de fond, et un coin bruyant cesse
 * d'être lu au moment où il annonce un échec.
 *
 * ── Pourquoi un store et pas un contexte ──────────────────────────────────
 *
 * Un contexte obligerait chaque appelant à être un composant et à passer par
 * un hook. Or la moitié des sites d'appel sont des fonctions asynchrones
 * appelées depuis un `onClick`, parfois dans un `catch` de service. `toast.ok`
 * s'appelle de n'importe où, ce qui est la seule façon d'en mettre partout
 * sans réécrire les composants autour.
 */
export type ToastTone = 'ok' | 'bad' | 'info'

export type Toast = {
  id: number
  tone: ToastTone
  message: string
  /** Le détail que l'on garde sous le message : un code, un nom, une raison. */
  detail?: string
}

type ToastStore = {
  toasts: Toast[]
  push: (toast: Omit<Toast, 'id'>) => void
  dismiss: (id: number) => void
}

/*
 * Trois à l'écran, pas plus.
 *
 * Une boucle qui échoue vingt fois — une purge, un envoi en série — remplirait
 * la colonne et masquerait l'interface avec laquelle il faut réparer. Les plus
 * ANCIENS partent : le dernier message est celui qui décrit ce qui vient de se
 * passer.
 */
const MAX = 3

/* Un succès s'efface seul ; un échec reste plus longtemps, parce qu'il porte
   souvent un code qu'on veut recopier. */
const LIFE: Record<ToastTone, number> = { ok: 3200, info: 4000, bad: 7000 }

let nextId = 1

export const useToasts = create<ToastStore>((set) => ({
  toasts: [],
  push: (toast) => {
    const id = nextId++
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }].slice(-MAX) }))
    /*
     * Le retrait est programmé ici et non dans le composant : un message doit
     * disparaître même si l'écran qui l'a émis est démonté entre-temps — ce
     * qui est le cas courant, puisque beaucoup de ces gestes ferment un
     * tiroir ou changent de page.
     */
    setTimeout(() => set((state) => ({ toasts: state.toasts.filter((row) => row.id !== id) })), LIFE[toast.tone])
  },
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((row) => row.id !== id) })),
}))

/**
 * L'appel court, depuis n'importe où — y compris hors d'un composant.
 *
 * `toast.bad(message, caught)` déballe l'erreur au passage : les erreurs de
 * `api.ts` portent un `code`, et un code est cherchable là où « une erreur est
 * survenue » ne l'est pas.
 */
export const toast = {
  ok: (message: string, detail?: string) => useToasts.getState().push({ tone: 'ok', message, detail }),
  info: (message: string, detail?: string) => useToasts.getState().push({ tone: 'info', message, detail }),
  bad: (message: string, caught?: unknown) =>
    useToasts.getState().push({ tone: 'bad', message, detail: reasonOf(caught) }),
}

function reasonOf(caught: unknown): string | undefined {
  if (!caught) return undefined
  const err = caught as { code?: string; status?: number; message?: string }
  if (err.code) return err.status ? `${err.code} (${err.status})` : err.code
  if (err.status) return String(err.status)
  return err.message
}
